/**
 * Shared wallet state so Header, Profile, BuyPack, etc. all see the same account.
 * Without this, each component that calls useWallet() had its own state, so connecting in Header didn't update Profile.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import { ethers } from "ethers";
import { BSC_CHAIN_ID, NETWORK_CONFIG, RPC_URLS } from "../constants";
import { useTranslation } from "../hooks/useTranslation";
import { clearReferralAddress } from "../utils/referralStorage";
import { useAppKitAccount, useAppKit, useAppKitProvider, useAppKitState, useDisconnect } from "@reown/appkit/react";

const WalletContext = createContext(null);

function isTokenPocketProvider(provider) {
    if (!provider) return false;
    const info = [
        provider?.info?.name,
        provider?.info?.rdns,
        provider?.name,
        provider?.rdns,
        provider?.providerInfo?.name,
        provider?.providerInfo?.rdns,
        provider?.walletMeta?.name,
        provider?.walletMeta?.rdns,
        provider?.constructor?.name,
    ].filter(Boolean).join(" ").toLowerCase();
    return Boolean(provider.isTokenPocket || provider.isTokenpocket || info.includes("tokenpocket"));
}

function isTokenPocketEnvironment() {
    if (typeof window === "undefined") return false;
    const ua = (navigator.userAgent || "").toLowerCase();
    const eth = window.ethereum;
    return Boolean(
        ua.includes("tokenpocket") ||
        ua.includes("tpwallet") ||
        window.tokenpocket ||
        window.TokenPocket ||
        isTokenPocketProvider(eth) ||
        eth?.providers?.some?.(isTokenPocketProvider)
    );
}

async function assertTokenPocketCanSign(provider, account) {
    if (!isTokenPocketEnvironment()) return true;
    if (!provider || !account) return false;
    const message = `Pokemon TCG wallet ownership check: ${account}`;
    try {
        await provider.request({ method: "personal_sign", params: [message, account] });
        return true;
    } catch (error) {
        console.warn("TP Wallet signature capability check failed:", error);
        return false;
    }
}
function clearWalletSessionStorage() {
    if (typeof window === "undefined") return;
    localStorage.removeItem("connectedAccount");
    localStorage.removeItem("pendingReferralAddress");
    localStorage.setItem("manuallyDisconnected", "true");
    Object.keys(localStorage).forEach((key) => {
        if (key.includes("wc@") || key.includes("@w3m") || key.includes("appkit") || key.includes("walletconnect")) {
            localStorage.removeItem(key);
        }
    });
    Object.keys(sessionStorage || {}).forEach((key) => {
        if (key.includes("wc@") || key.includes("@w3m") || key.includes("appkit") || key.includes("walletconnect")) {
            sessionStorage.removeItem(key);
        }
    });
}

export function WalletProvider({ children }) {
    const [injectedAccount, setInjectedAccount] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [chainId, setChainId] = useState(null);
    const [isManuallyDisconnected, setIsManuallyDisconnected] = useState(() => {
        return localStorage.getItem("manuallyDisconnected") === "true";
    });
    const [showNetworkSwitchModal, setShowNetworkSwitchModal] = useState(false);
    const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
    const { t } = useTranslation();
    const runInjectedCheckRef = useRef(false);
    const tokenPocketCleanupRef = useRef(false);
    const [reownGracePeriodEnded, setReownGracePeriodEnded] = useState(false);

    const { address: reownAddress, isConnected: isReownConnected } = useAppKitAccount({ namespace: "eip155" }) || {};
    const { open: openReownModal } = useAppKit() || {};
    const { walletProvider: reownWalletProvider } = useAppKitProvider("eip155") || {};
    const appKitState = useAppKitState();
    const reownInitialized = appKitState?.initialized;
    const disconnectReown = useDisconnect?.();

    const isReownInitializing = reownInitialized === false;
    const isReownActive = isReownConnected && !!reownAddress;

    // While Reown is restoring, use cached account from localStorage so the UI doesn't show "Sign in" / loading
    const cachedAccount = useRef(localStorage.getItem("connectedAccount") || null);

    const account = isReownActive
        ? reownAddress
        : (reownInitialized && reownGracePeriodEnded ? injectedAccount : cachedAccount.current);

    let apiBaseUrl;

    if (process.env.REACT_APP_ENV == 'staging') {
        apiBaseUrl = process.env.REACT_APP_API_URL || "https://public-buynode-pokemon.madxapp.com";
    } else {
        apiBaseUrl = process.env.REACT_APP_API_URL || "https://public-buynode-pokemon.tcg.zone";
    }

    // Keep the cache in sync
    useEffect(() => {
        if (isReownActive || (reownInitialized && reownGracePeriodEnded && injectedAccount)) {
            cachedAccount.current = account;
        } else if (reownInitialized && reownGracePeriodEnded && !isReownActive && !injectedAccount) {
            cachedAccount.current = null;
        }
    }, [isReownActive, reownInitialized, reownGracePeriodEnded, injectedAccount, account]);

    useEffect(() => {
        if (reownInitialized !== true) {
            setReownGracePeriodEnded(false);
            return;
        }
        const timer = setTimeout(() => setReownGracePeriodEnded(true), 300);
        return () => clearTimeout(timer);
    }, [reownInitialized]);

    useEffect(() => {
        if (typeof window === "undefined" || !window.ethereum) return;
        const rawRequest = window.ethereum.request.bind(window.ethereum);
        let lastToastTime = 0;
        const TOAST_COOLDOWN_MS = 5000;
        const isRpcError = (err) => {
            if (!err) return false;
            const code = err.code ?? err.error?.code;
            const msg = (err.message || err.error?.message || "").toLowerCase();
            return code === -32603 || code === "SERVER_ERROR" || msg.includes("internal json-rpc") || msg.includes("json-rpc error");
        };
        window.ethereum.request = async (args) => {
            try {
                return await rawRequest(args);
            } catch (err) {
                if (isRpcError(err) && Date.now() - lastToastTime > TOAST_COOLDOWN_MS) {
                    lastToastTime = Date.now();
                    toast.error(t("header.rpcErrorSwitchRpc"), { duration: 6000 });
                }
                throw err;
            }
        };
        return () => { window.ethereum.request = rawRequest; };
    }, [t]);

    useEffect(() => {
        const shouldRun = reownInitialized === true && reownGracePeriodEnded && !isReownActive && !isManuallyDisconnected && !!window.ethereum;
        runInjectedCheckRef.current = shouldRun;
        if (!shouldRun) return;
        const timeoutId = window.setTimeout(() => {
            if (!runInjectedCheckRef.current || !window.ethereum) return;
            const checkWalletConnection = async () => {
                if (!runInjectedCheckRef.current || !window.ethereum) return;
                try {
                    const accounts = await window.ethereum.request({ method: "eth_accounts" });
                    if (accounts.length > 0) {
                        const canSign = await assertTokenPocketCanSign(window.ethereum, accounts[0]);
                        if (!canSign) {
                            clearWalletSessionStorage();
                            setInjectedAccount(null);
                            setChainId(null);
                            setIsManuallyDisconnected(true);
                            clearReferralAddress();
                            toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
                            return;
                        }
                        setInjectedAccount(accounts[0]);
                        localStorage.setItem("connectedAccount", accounts[0]);
                        setIsManuallyDisconnected(false);
                        localStorage.removeItem("manuallyDisconnected");
                    } else {
                        setInjectedAccount(null);
                        localStorage.removeItem("connectedAccount");
                    }
                    const currentChainId = await window.ethereum.request({ method: "eth_chainId" });
                    setChainId(currentChainId);
                    setShowNetworkSwitchModal(currentChainId !== `0x${BSC_CHAIN_ID.toString(16)}`);
                } catch (error) {
                    console.error("Error checking wallet connection:", error);
                }
            };
            checkWalletConnection();
        }, 400);
        return () => clearTimeout(timeoutId);
    }, [isManuallyDisconnected, isReownActive, reownInitialized, reownGracePeriodEnded]);

    useEffect(() => {
        if (account) localStorage.setItem("connectedAccount", account);
        else localStorage.removeItem("connectedAccount");
    }, [account]);

    useEffect(() => {
        if (!window.ethereum || isReownActive || isReownInitializing) return;
        const handleAccountsChanged = async (accounts) => {
            if (accounts.length === 0) {
                setInjectedAccount(null);
                setIsManuallyDisconnected(true);
                localStorage.removeItem("connectedAccount");
                localStorage.removeItem("pendingReferralAddress");
                localStorage.setItem("manuallyDisconnected", "true");
                toast.error(t("header.walletDisconnected"));
            } else if (accounts[0] !== injectedAccount) {
                const canSign = await assertTokenPocketCanSign(window.ethereum, accounts[0]);
                if (!canSign) {
                    clearWalletSessionStorage();
                    setInjectedAccount(null);
                    setChainId(null);
                    setIsManuallyDisconnected(true);
                    clearReferralAddress();
                    toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
                    return;
                }
                setInjectedAccount(accounts[0]);
                setIsManuallyDisconnected(false);
                localStorage.setItem("connectedAccount", accounts[0]);
                localStorage.removeItem("pendingReferralAddress");
                localStorage.removeItem("manuallyDisconnected");
                toast.success(t("header.accountSwitched"));
            }
        };
        const handleChainChanged = (newChainId) => {
            setChainId(newChainId);
            setShowNetworkSwitchModal(newChainId !== `0x${BSC_CHAIN_ID.toString(16)}`);
            window.location.reload();
        };
        const handleDisconnect = () => {
            setInjectedAccount(null);
            setIsManuallyDisconnected(true);
            localStorage.removeItem("connectedAccount");
            localStorage.removeItem("pendingReferralAddress");
            localStorage.setItem("manuallyDisconnected", "true");
            toast.error(t("header.walletDisconnected"));
        };
        window.ethereum.on("accountsChanged", handleAccountsChanged);
        window.ethereum.on("chainChanged", handleChainChanged);
        window.ethereum.on("disconnect", handleDisconnect);
        return () => {
            if (window.ethereum.removeListener) {
                window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
                window.ethereum.removeListener("chainChanged", handleChainChanged);
                window.ethereum.removeListener("disconnect", handleDisconnect);
            }
        };
    }, [injectedAccount, isReownActive, isReownInitializing, t]);

    const connectWallet = useCallback(async () => {
        if (!window.ethereum) {
            toast.error(t("header.pleaseInstallMetaMask"));
            return false;
        }
        setIsConnecting(true);
        try {
            setIsManuallyDisconnected(false);
            localStorage.removeItem("manuallyDisconnected");
            const requestFn = typeof window !== "undefined" && window.__ORIGINAL_ETHEREUM_REQUEST__
                ? window.__ORIGINAL_ETHEREUM_REQUEST__
                : window.ethereum.request.bind(window.ethereum);
            const accounts = await requestFn({ method: "eth_requestAccounts" });
            if (accounts.length > 0) {
                const canSign = await assertTokenPocketCanSign(window.ethereum, accounts[0]);
                if (!canSign) {
                    clearWalletSessionStorage();
                    setInjectedAccount(null);
                    setChainId(null);
                    setIsManuallyDisconnected(true);
                    clearReferralAddress();
                    toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
                    return false;
                }

                // Register account with backend after successful wallet connect
                try {
                    const res = await fetch(`${apiBaseUrl}/publicApi/registerAccount`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ wallet_address: accounts[0] }),
                    });
                    const responseText = await res.json();

                    if (!res.ok) {
                        throw new Error(responseText.message || responseText.error || `HTTP error! status: ${res.status}`);
                    }
                } catch (apiError) {
                    console.warn("registerAccount API call failed:", apiError);
                }

                setInjectedAccount(accounts[0]);
                localStorage.setItem("connectedAccount", accounts[0]);
                const currentChainId = await window.ethereum.request({ method: "eth_chainId" });
                setChainId(currentChainId);
                setShowNetworkSwitchModal(currentChainId !== `0x${BSC_CHAIN_ID.toString(16)}`);
                toast.success(t("header.walletConnected"));
                return true;
            }
            return false;
        } catch (error) {
            if (error.code === 4001) toast.error(t("header.connectionRejected"));
            else toast.error(t("header.walletConnectFailed"));
            console.error("Wallet connection error:", error);
            return false;
        } finally {
            setIsConnecting(false);
        }
    }, [t]);

    // const disconnectWallet = useCallback(async () => {
    //     try {
    //         if (window.ethereum?.request) {
    //             try {
    //                 await window.ethereum.request({ method: "wallet_revokePermissions", params: [{ eth_accounts: {} }] });
    //             } catch (_) { }
    //         }
    //         if (isReownActive && disconnectReown?.disconnect) await disconnectReown.disconnect();
    //         setInjectedAccount(null);
    //         setChainId(null);
    //         setIsManuallyDisconnected(true);
    //         localStorage.removeItem("connectedAccount");
    //         localStorage.removeItem("pendingReferralAddress");
    //         localStorage.setItem("manuallyDisconnected", "true");
    //         clearReferralAddress();
    //         toast.success(t("header.walletDisconnected"));
    //     } catch (error) {
    //         console.error("Error disconnecting wallet:", error);
    //         setInjectedAccount(null);
    //         setChainId(null);
    //         setIsManuallyDisconnected(true);
    //         localStorage.removeItem("connectedAccount");
    //         localStorage.removeItem("pendingReferralAddress");
    //         localStorage.setItem("manuallyDisconnected", "true");
    //         clearReferralAddress();
    //         toast.success(t("header.walletDisconnected"));
    //     }
    // }, [t, isReownActive, disconnectReown]);
    const disconnectWallet = useCallback(async () => {
        try {
            // 1. Standard disconnects
            if (disconnectReown?.disconnect) await disconnectReown.disconnect();

            // 2. Clear your state
            setInjectedAccount(null);
            localStorage.removeItem("connectedAccount");
            localStorage.setItem("manuallyDisconnected", "true");

            // 3. Clear ALL Reown/WalletConnect keys
            Object.keys(localStorage).forEach(key => {
                if (key.includes('wc@') || key.includes('@w3m') || key.includes('appkit')) {
                    localStorage.removeItem(key);
                }
            });

            toast.success(t("header.walletDisconnected"));

            // 4. THE FIX: Force a reload after a short delay
            // This clears the React Context memory where 'undefined' is stuck
            setTimeout(() => {
                window.location.reload();
            }, 500);

        } catch (error) {
            console.error("Disconnect error:", error);
            window.location.reload(); // Force fix on error
        }
    }, [t, disconnectReown]);

    useEffect(() => {
        const verifyConnectedTokenPocketWallet = async () => {
            if (!isTokenPocketEnvironment() || tokenPocketCleanupRef.current) return;
            const connectedAccount = account || localStorage.getItem("connectedAccount");
            if (!connectedAccount) return;
            const canSign = await assertTokenPocketCanSign(window.ethereum, connectedAccount);
            if (canSign) {
                tokenPocketCleanupRef.current = true;
                return;
            }
            tokenPocketCleanupRef.current = true;
            clearWalletSessionStorage();
            setInjectedAccount(null);
            setChainId(null);
            setIsManuallyDisconnected(true);
            setShowNetworkSwitchModal(false);
            clearReferralAddress();
            if (disconnectReown?.disconnect) {
                disconnectReown.disconnect().catch(() => {});
            }
            toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
        };
        verifyConnectedTokenPocketWallet();
    }, [account, disconnectReown]);

    const switchNetwork = useCallback(async (targetChainId = BSC_CHAIN_ID) => {
        if (!window.ethereum) {
            toast.error(t("header.installMetaMask"));
            return false;
        }
        setIsSwitchingNetwork(true);
        try {
            await window.ethereum.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: `0x${targetChainId.toString(16)}` }],
            });
            setShowNetworkSwitchModal(false);
            return true;
        } catch (error) {
            if (error.code === 4902) {
                try {
                    await window.ethereum.request({
                        method: "wallet_addEthereumChain",
                        params: [NETWORK_CONFIG.BSC_MAINNET],
                    });
                    setShowNetworkSwitchModal(false);
                    return true;
                } catch (addError) {
                    toast.error(t("header.failedToAddBSCToWallet"));
                    return false;
                }
            } else if (error.code === 4001) {
                toast.error(t("header.connectionRejected"));
                return false;
            } else {
                toast.error(t("header.failedToSwitchNetwork"));
            }
            return false;
        } finally {
            setIsSwitchingNetwork(false);
        }
    }, [t]);

    useEffect(() => {
        if (isReownActive || isReownInitializing || !window.ethereum || !account) {
            setShowNetworkSwitchModal(false);
            return;
        }
        const checkNetwork = async () => {
            try {
                const currentChainId = await window.ethereum.request({ method: "eth_chainId" });
                setShowNetworkSwitchModal(currentChainId !== `0x${BSC_CHAIN_ID.toString(16)}`);
            } catch (error) {
                console.error("Error checking network:", error);
            }
        };
        checkNetwork();
    }, [account, chainId, isReownActive, isReownInitializing]);

    const checkNetwork = useCallback(async () => {
        if (!window.ethereum) return false;
        try {
            const chainId = await window.ethereum.request({ method: "eth_chainId" });
            const currentChainId = parseInt(chainId, 16);
            if (currentChainId !== BSC_CHAIN_ID) {
                toast.error(t("header.pleaseSwitchToBSC"));
                return false;
            }
            return true;
        } catch (error) {
            console.error("Network check error:", error);
            return false;
        }
    }, [t]);

    const copyAddress = useCallback(() => {
        if (account) {
            navigator.clipboard.writeText(account);
            toast.success(t("header.addressCopied"));
        }
    }, [account]);

    const isMetaMaskInAppBrowser = useCallback(() => {
        if (typeof window === "undefined") return false;
        const ua = (window.navigator?.userAgent || "").toLowerCase();
        const hasMetaMaskFlag = !!window.ethereum?.isMetaMask;
        return hasMetaMaskFlag && (ua.includes("metamask") || ua.includes("metamaskmobile"));
    }, []);

    const tryOpenExternalBrowser = useCallback(() => {
        if (typeof window === "undefined") return false;
        const currentUrl = window.location.href;

        // Try direct window.open first.
        const popup = window.open(currentUrl, "_blank", "noopener,noreferrer");
        if (popup && !popup.closed) {
            return true;
        }

        // Fallback: anchor click (works in some in-app browsers).
        const link = document.createElement("a");
        link.href = currentUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer external";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
    }, []);

    // const openReown = useCallback(() => {
    //     if (isMetaMaskInAppBrowser()) {
    //         try {
    //             const opened = tryOpenExternalBrowser();
    //             if (opened) {
    //                 toast("For Google login, continue in your external browser.");
    //                 return;
    //             }
    //         } catch (error) {
    //             console.warn("Failed to open external browser from MetaMask in-app browser:", error);
    //         }
    //     }

    //     if (typeof openReownModal === "function") {
    //         openReownModal({ view: "Connect" });
    //     }
    // }, [openReownModal, isMetaMaskInAppBrowser, tryOpenExternalBrowser]);

    const openReown = useCallback(async () => {
        // If any injected web3 wallet is detected (MetaMask, TP Wallet, Trust Wallet,
        // Binance Wallet, or ANY extension/dApp browser that injects window.ethereum),
        // bypass the Reown modal entirely and connect directly.
        // This prevents the 'identity/undefined' race condition that occurs when
        // Reown's syncIdentity fires before the address resolves on injected wallets.
        if (typeof window !== "undefined" && window.ethereum) {
            const success = await connectWallet();
            if (success) return;
            // If connectWallet fails (e.g. user rejected), do not fall back to modal —
            // the error toast is already shown inside connectWallet.
            return;
        }

        // No injected wallet detected — show the Reown modal so the user can connect
        // via WalletConnect QR code, email, or social login.
        if (typeof openReownModal === "function") {
            try {
                openReownModal({ view: "Connect" });
            } catch (error) {
                console.error("Reown Modal failed to open:", error);
            }
        }
    }, [openReownModal, connectWallet]);

    const getSigner = useCallback(async () => {
        if (isReownActive && reownWalletProvider && reownAddress) {
            const provider = new ethers.BrowserProvider(reownWalletProvider, BSC_CHAIN_ID);
            return new ethers.JsonRpcSigner(provider, reownAddress);
        }
        if (reownInitialized && !isReownActive && !reownGracePeriodEnded) {
            throw new Error("Wallet is connecting…");
        }
        if (typeof window !== "undefined" && window.ethereum) {
            const provider = new ethers.BrowserProvider(window.ethereum, BSC_CHAIN_ID);
            if (injectedAccount) return new ethers.JsonRpcSigner(provider, injectedAccount);
            throw new Error("No wallet available");
        }
        throw new Error("No wallet available");
    }, [isReownActive, reownWalletProvider, reownAddress, reownInitialized, reownGracePeriodEnded, injectedAccount]);

    const getProvider = useCallback(async () => {
        if (isReownActive && reownWalletProvider) {
            return new ethers.BrowserProvider(reownWalletProvider, BSC_CHAIN_ID);
        }
        if (reownInitialized && !isReownActive && !reownGracePeriodEnded) {
            return new ethers.JsonRpcProvider(RPC_URLS?.BSC_MAINNET?.[0] || "https://bsc-dataseed1.binance.org/");
        }
        if (typeof window !== "undefined" && window.ethereum) {
            return new ethers.BrowserProvider(window.ethereum);
        }
        return new ethers.JsonRpcProvider(RPC_URLS?.BSC_MAINNET?.[0] || "https://bsc-dataseed1.binance.org/");
    }, [isReownActive, reownWalletProvider, reownInitialized, reownGracePeriodEnded]);


    const value = useMemo(() => ({
        account,
        isConnecting,
        chainId,
        connectWallet,
        disconnectWallet,
        switchNetwork,
        checkNetwork,
        copyAddress,
        openReown,
        getSigner,
        getProvider,
        isReownActive,
        isReownInitializing,
        reownInitialized,
        isConnected: !!account,
        isCorrectNetwork: chainId === `0x${BSC_CHAIN_ID.toString(16)}`,
        isTokenPocketBlocked: false,
        showNetworkSwitchModal,
        setShowNetworkSwitchModal,
        isSwitchingNetwork,
    }), [
        account,
        isConnecting,
        chainId,
        connectWallet,
        disconnectWallet,
        switchNetwork,
        checkNetwork,
        copyAddress,
        openReown,
        getSigner,
        getProvider,
        isReownActive,
        isReownInitializing,
        reownInitialized,
        showNetworkSwitchModal,
        isSwitchingNetwork
    ]);

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
}

export function useWallet() {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error("useWallet must be used within a WalletProvider");
    }
    return context;
}
